import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, X, Zap, Shield } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';

const MdLicenseBanner = () => {
  const { tenantVersion } = useAuthStore();
  const [license, setLicense] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const res = await fetchWithTenant('/api/md/license');
        if (res.ok) {
          const data = await res.json();
          setLicense(data);
        } else {
          // 403 or other error - hide banner
          setLicense(null);
        }
      } catch (e) {
        console.error('Failed to fetch license:', e);
      }
    };
    fetchLicense();
  }, [tenantVersion]);

  // Reset dismissed state on tenant switch
  useEffect(() => {
    setDismissed(false);
  }, [tenantVersion]);

  if (!license || dismissed) return null;

  // No license at all
  if (!license.hasLicense) {
    return (
      <div className="md-license-banner no-license">
        <div className="md-license-banner-content">
          <Shield size={18} />
          <span>{license.message}</span>
        </div>
      </div>
    );
  }

  // Expired
  if (license.isExpired) {
    return (
      <div className="md-license-banner expired">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>
            {license.sku === 'TRIAL'
              ? 'Your CCM Monitor trial has expired. Upgrade to continue monitoring your network.'
              : 'Your CCM Monitor license has expired. Please contact your administrator to renew.'}
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={() => window.open('https://serverli.com/pricing', '_blank')}>
            <Zap size={14} /> Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  // Trial active - show days remaining
  if (license.status === 'TRIAL' && license.trialDaysRemaining !== null) {
    const urgent = license.trialDaysRemaining <= 2;
    return (
      <div className={`md-license-banner trial ${urgent ? 'urgent' : ''}`}>
        <div className="md-license-banner-content">
          <Clock size={18} />
          <span>
            <strong>{license.trialDaysRemaining} day{license.trialDaysRemaining !== 1 ? 's' : ''}</strong> remaining in your CCM Monitor trial.
            {license.usage && ` Monitoring ${license.usage.devices} of ${license.maxDevices} devices.`}
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={() => window.open('https://serverli.com/pricing', '_blank')}>
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Over device limit
  if (license.isOverLimit) {
    return (
      <div className="md-license-banner warning">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>
            You've exceeded your device limit ({license.usage?.devices}/{license.maxDevices}).
            Some features may be restricted. Upgrade your plan to add more devices.
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={() => window.open('https://serverli.com/pricing', '_blank')}>
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Near limit warning
  if (license.isNearLimit) {
    return (
      <div className="md-license-banner info">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>
            You're approaching your device limit ({license.usage?.devices}/{license.maxDevices}).
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default MdLicenseBanner;
